import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

import Helmet from 'react-helmet';

import Layout, { ContentSection } from '~components/Layout';
import StateRegisterInfo from '~components/common/StateRegisterInfo';
import StateVoteByMailInfo from '~components/common/StateVoteByMailInfo';
import StateContactInfo from '~components/common/StateContactInfo';
import StateExternalLinks from '~components/common/StateExternalLinks';
import LastUpdated from '~components/common/LastUpdated';
import StatesListSection from '../components/StatesList/StatesListSection';
import StateAlerts from '../components/common/StateAlerts';
import HeaderButtonMenu from '../components/home/HeaderButtonMenu';
import StateDeadlinesSection from '../components/common/StateDeadlinesSection';
import StateInPersonVotingInfo from '../components/common/StateInPersonVotingInfo';

const slugify = require('slugify');

function StatePage({ data }) {
  const state = data.voteAmericaState;
  const title = `How to Vote in ${state.name} - VoteAmerica`;
  const description = `Learn how to vote in ${state.name}. Voter registration, absentee ballots, early voting, and voter ID information.`;
  const image = `share/v6/states/${state.code}.png`;

  const stateDataArray = data.allVoteAmericaState.edges;
  const statePageListData = stateDataArray.map((stateItem) => ({
    key: slugify(stateItem.node.name.toLowerCase()),
    linkText: `How to vote in ${stateItem.node.name}`,
    linkUrl: `/how-to-vote/${slugify(stateItem.node.name.toLowerCase())}/`,
  }));

  // reduce state_information array to hash, so we can access more easily in template
  const info = state.state_information.reduce((map, obj) => {
    if (obj && obj.field) {
      // eslint-disable-next-line no-param-reassign
      map[obj.field.slug] = {
        text: obj.text,
        html: obj.html,
        bool_value: obj.bool_value,
        multiselect_value: obj.multiselect_value,
        footnotes: obj.footnotes,
      };
    }
    return map;
  }, {});
  const overrides = data.allVoteAmericaOverride.nodes;

  return (
    <Layout title={title} description={description} image={image}>
      <Helmet>
        <script type='application/ld+json'>
          {`
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": "How to vote",
                "item": "https://www.voteamerica.org/how-to-vote/"
              },{
                "@type": "ListItem",
                "position": 2,
                "name": "How to vote in ${state.name}"
              }]
            }
          `}
        </script>
      </Helmet>
      <div>
        <ContentSection width='mid' isZebraStriped>
          <LastUpdated array={state.state_information} />
          <h1>How to vote in {state.name}</h1>
          <div className='space-y-10'>
            <StateAlerts
              stateInfo={info}
              showRegistrationAlerts
              showVbmAlerts
            />

            <HeaderButtonMenu
              state={state.name}
              slug={slugify(state.name.toLowerCase())}
              tools={[
                'verify',
                'register',
                'absentee',
                'upcoming',
                'futurevoter',
                'remind',
                'locate',
                'trackMail',
                'leo',
              ]}
            />

            <div className='py-5'>
              <p className='mb-2.5 font-bold'>On this page</p>
              <ul className='mb-0 space-y-1.5'>
                <li>
                  <a href='#deadlines'>Voting deadlines</a>
                </li>
                <li>
                  <a href='#registration'>Voter registration</a>
                </li>
                <li>
                  <a href='#vbm'>Absentee and mail ballots</a>
                </li>
                <li>
                  <a href='#in-person-voting'>Voting in person</a>
                </li>
                <li>
                  <a href='#contact-info'>State and local election offices</a>
                </li>
                <li>
                  <a href='#external-resources'>External resources</a>
                </li>
              </ul>
            </div>
          </div>
        </ContentSection>
        <StateDeadlinesSection state={state} stateInfo={info} />
        <StateRegisterInfo info={info} state={state} styleTitleAsH1={false} />
        <StateVoteByMailInfo
          info={info}
          overrides={overrides}
          state={state}
          styleTitleAsH1={false}
        />
        <StateInPersonVotingInfo state={state} info={info} />
        <StateContactInfo state={state} info={info} />
        <StateExternalLinks state={state} info={info} />
        <StatesListSection heading='How to vote' data={statePageListData} />
      </div>
    </Layout>
  );
}

StatePage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.object,
    voteAmericaState: PropTypes.shape({
      name: PropTypes.string,
      code: PropTypes.string,
      state_information: PropTypes.array,
    }),
    allVoteAmericaState: PropTypes.shape({
      edges: PropTypes.array,
    }),
    allVoteAmericaOverride: PropTypes.shape({
      nodes: PropTypes.array,
    }),
  }),
};

export default StatePage;

export const pageQuery = graphql`
  query StateByName($id: String) {
    voteAmericaState(code: { eq: $id }) {
      name
      code
      state_information {
        html
        text
        bool_value
        multiselect_value
        footnotes
        field {
          slug
        }
        modified_at
      }
    }
    allVoteAmericaState(sort: { name: ASC }) {
      edges {
        node {
          name
        }
      }
    }
    allVoteAmericaOverride(
      filter: {
        state: { eq: $id }
        field: { slug: { in: ["gov_tool_absentee_request"] } }
      }
    ) {
      nodes {
        value
        state
        field {
          slug
        }
        region {
          name
        }
      }
    }
  }
`;
